import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FormModal from '../../components/Modals/FormModal';
import InputField from '../../components/Forms/Input/InputField';
import SelectField from '../../components/Forms/Input/SelectField';
import { useDocumentType } from '../../hooks/useDocumentType';
import { useBranch } from '../../hooks/branches/useBranch';
import { useAllowedOptions } from '../../hooks/settings/useAllowedOptions';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Record<string, any>) => void;
}

const DocumentSequenceRegistrationPopup: React.FC<Props> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const { fetchBranchDropdown, branchDropdown } = useBranch();
  const { fetchDocumentTypeDropdown, documentTypeDropdown } = useDocumentType();
  const { getCodes, fetchCodesByModuleAndTarget } = useAllowedOptions();

  const validationSchema = Yup.object({
    branchId: Yup.number()
      .min(1, 'Seleccione una sucursal')
      .required('Campo obligatorio'),
    documentTypeId: Yup.number()
      .min(1, 'Seleccione un tipo de documento')
      .required('Campo obligatorio'),
    prefix: Yup.string().required('El prefijo es obligatorio'),
    currentNumber: Yup.number()
      .min(0, 'Debe ser mayor o igual a 0')
      .required('Campo obligatorio'),
  });

  const formik = useFormik({
    initialValues: {
      branchId: 0,
      documentTypeId: 0,
      prefix: 'DTE',
      currentNumber: 0,
    },
    validationSchema,
    onSubmit: (values) => {
      const payload = {
        branchId: Number(values.branchId),
        documentTypeId: Number(values.documentTypeId),
        prefix: values.prefix.toUpperCase(),
        currentNumber: Number(values.currentNumber),
      };
      onSubmit(payload);
    },
  });

  useEffect(() => {
    if (isOpen) {
      fetchBranchDropdown();
      fetchDocumentTypeDropdown();
      fetchCodesByModuleAndTarget('AUTHORIZED_SERIES', 'DOCUMENT_TYPE');
      formik.resetForm();
    }
  }, [isOpen]);

  const allowedCodes = getCodes('AUTHORIZED_SERIES', 'DOCUMENT_TYPE');
  const filteredDocumentTypes = documentTypeDropdown.filter((doc) =>
    allowedCodes.includes(doc.code)
  );

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Registrar Número de Control Interno"
      maxWidth="max-w-3xl"
    >
      <form
        onSubmit={formik.handleSubmit}
        className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2"
      >
        {/* 🏢 Sucursal */}
        <SelectField
          label="Sucursal"
          name="branchId"
          iconKey="branch"
          value={formik.values.branchId}
          onChange={formik.handleChange}
          options={(branchDropdown || []).map((b) => ({
            label: b.name,
            value: b.id.toString(),
          }))}
          error={
            formik.touched.branchId && formik.errors.branchId
              ? formik.errors.branchId
              : undefined
          }
          placeholder="Seleccione"
        />

        {/* 📄 Tipo de Documento */}
        <SelectField
          label="Tipo de Documento"
          name="documentTypeId"
          iconKey="document"
          value={formik.values.documentTypeId}
          onChange={formik.handleChange}
          options={(filteredDocumentTypes || []).map((d) => ({
            label: d.name,
            value: d.id.toString(),
          }))}
          error={
            formik.touched.documentTypeId && formik.errors.documentTypeId
              ? formik.errors.documentTypeId
              : undefined
          }
          placeholder="Seleccione"
        />

        {/* 🔤 Prefijo */}
        <InputField
          label="Prefijo"
          name="prefix"
          iconKey="hash"
          value={formik.values.prefix}
          onChange={(e) =>
            formik.setFieldValue('prefix', e.target.value.toUpperCase())
          }
          error={
            formik.touched.prefix && formik.errors.prefix
              ? formik.errors.prefix
              : undefined
          }
          type="text"
          disabled
        />

        {/* 🔢 Correlativo actual */}
        <InputField
          label="Correlativo Actual"
          name="currentNumber"
          type="number"
          iconKey="hash"
          value={formik.values.currentNumber}
          onChange={formik.handleChange}
          error={
            formik.touched.currentNumber && formik.errors.currentNumber
              ? formik.errors.currentNumber
              : undefined
          }
          disabled
        />

        <div className="md:col-span-2">
          <button
            type="submit"
            className="w-full rounded bg-primary px-4.5 py-2.5 font-medium text-white hover:bg-opacity-90"
          >
            Registrar Secuencia
          </button>
        </div>
      </form>
    </FormModal>
  );
};

export default DocumentSequenceRegistrationPopup;
